import { getReferenceString, MedplumClient } from "@medplum/core";
import { BundleEntry, Condition, List, Observation } from "@medplum/fhirtypes";

import { formatISO } from "date-fns";

import { Systems } from "@/fhir/enums/systems";
import { middleOfDay } from "@/utils/datetime";

import { getPregnancyRecordListIfMatch } from "../utils/record";

import { fetchPregnancyConditionForPregnancyRecord } from "./condition";
import { buildCancelEDDBundleEntries } from "./edd";

/**
 * Builds the `BundleEntry` resources needed to close (retire) an active pregnancy record.
 *
 * Always produces a `PUT` entry that sets the `List` status to `"retired"`. When a
 * linked pregnancy `Condition` is found via `_has:List:item:_id`, a second `PUT` entry
 * is appended that transitions `clinicalStatus` to `"resolved"` and sets
 * `abatementDateTime` to the provided `closeOutDate` (or today at noon when omitted).
 *
 * @param medplum - Medplum client used to look up the linked pregnancy `Condition`
 * @param options.list - The active pregnancy `List` to close; must have a non-empty `id`
 * @param options.closeOutDate - Optional ISO date string for the condition's
 *   `abatementDateTime`; defaults to today at noon when omitted
 * @returns Array of 0–2 `BundleEntry` items; empty when the list has no `id`
 */
export async function buildClosePregnancyRecordBundleEntry(
  medplum: MedplumClient,
  { list, closeOutDate }: { list: List; closeOutDate?: string },
): Promise<BundleEntry<List | Condition | Observation>[]> {
  if (!list.id || !list.subject) return [];

  const patientReferenceString = getReferenceString(list.subject);

  if (!patientReferenceString?.startsWith("Patient/")) return [];

  const listReferenceString = getReferenceString(list)!;
  const entry: BundleEntry<List | Condition | Observation>[] = [
    {
      resource: {
        ...list,
        status: "retired",
      },
      request: {
        method: "PUT",
        url: listReferenceString,
        ifMatch: getPregnancyRecordListIfMatch(list),
      },
    },
  ];

  const pregnancyCondition = await fetchPregnancyConditionForPregnancyRecord(medplum, list.id);

  if (pregnancyCondition) {
    const abatementDate = closeOutDate?.trim() || new Date();
    entry.push({
      resource: {
        ...pregnancyCondition,
        abatementDateTime: formatISO(middleOfDay(abatementDate)!),
        clinicalStatus: {
          coding: [{ system: Systems.HL7ConditionClinical, code: "resolved", display: "Resolved" }],
        },
      },
      request: {
        method: "PUT",
        url: getReferenceString(pregnancyCondition)!,
      },
    });
  }

  const cancelPreviousEDDEntries = await buildCancelEDDBundleEntries(
    medplum,
    patientReferenceString,
  );
  if (cancelPreviousEDDEntries.length) entry.push(...cancelPreviousEDDEntries);

  return entry;
}
